import { AxiosError, AxiosResponse } from 'axios';
import { get } from 'lodash';
import { normalize, Schema } from 'normalizr';

import { AnyObject } from '../../../app-core';
import { NormalisedData } from '../../../redux-helpers/types';
import { createActionSet } from '../../helpers/actions';
import { ActionSet, FreeShapeAction, Meta } from '../../types';
import { PROMISE_REQUEST } from './constants';
import {
  PromiseActionError,
  PromiseActionResponse,
  PromiseFlaggedAction,
  PromiseFlaggedMeta
} from './types';

/**
 * Checks if the action is a promise flagged action.
 * @param {FreeShapeAction} action accepted middleware action.
 * @returns {boolean}
 */
export const isPromiseRequest = (action: FreeShapeAction): action is PromiseFlaggedAction => get(action, 'meta.flag') === PROMISE_REQUEST;

/**
 * Gets the schema from the action.
 * @param {PromiseFlaggedAction} action
 * @returns {PromiseFlaggedAction.schema}
 */
export const getSchema = (action: PromiseFlaggedAction): PromiseFlaggedMeta['schema'] => action.meta.schema;

/**
 * Removes the metadata cleaning the action.
 * @param {PromiseFlaggedMeta} meta
 * @returns {Meta}
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const removeMetadata = ({ flag, ...filteredMeta }: PromiseFlaggedMeta): Meta => filteredMeta;

/**
 * Gets a pending action to be delivered.
 * @param {PromiseFlaggedAction} action
 * @returns {FreeShapeAction}
 */
export const getPendingAction = (action: PromiseFlaggedAction): FreeShapeAction => {
  const actionTypes: ActionSet = createActionSet(action.type);
  const meta = removeMetadata(action.meta);

  return {
    meta,
    type: actionTypes.PENDING
  };
};

/**
 * Gets a fullifed action to be delivered.
 * @param {PromiseFlaggedAction} action
 * @param {AxiosResponse} response
 * @returns {PromiseActionResponse}
 */
export const getFulfilledAction = (action: PromiseFlaggedAction, response: AxiosResponse): PromiseActionResponse => {
  const actionTypes: ActionSet = createActionSet(action.type);
  const meta = removeMetadata(action.meta);

  return {
    meta,
    type: actionTypes.FULFILLED,
    payload: response
  };
};

/**
 * Gets a rejected action to be delivered.
 * @param {PromiseFlaggedAction} action
 * @param {AxiosError} error
 * @returns {PromiseActionError}
 */
export const getRejectedAction = (action: PromiseFlaggedAction, error: AxiosError): PromiseActionError => {
  const actionTypes: ActionSet = createActionSet(action.type);
  const meta = removeMetadata(action.meta);

  return {
    meta,
    type: actionTypes.REJECTED,
    payload: error,
    error: true
  };
};

/**
 * Normalise the data with the schema to save it into the Redux state.
 * Do nothing if no schema provided.
 *
 * @function normaliseData
 * @param {AnyObject | []} data
 * @param {Schema} schema
 * @return {NormalisedData<{}>}
 */
export const normaliseData = (data: AnyObject | unknown[], schema: Schema): NormalisedData<unknown> => normalize(data, schema);
